import { useMemo, useState } from 'react';
import { ChevronsDown, ChevronsUp, ChevronDown, ChevronUp } from '@gravity-ui/icons';
import { Icon } from '@gravity-ui/uikit';
import { format } from 'date-fns';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';

import type { Estimate, Problem } from 'store/types/common';

import { Localization_RU } from './localization';

import styles from './styles.module.scss';

export interface ClientBlankEstimateTableProps {
	data: Data;
}

export interface Data {
	problems: Problem[];
	estimate?: Estimate[];
}

export const ClientBlankEstimateTable = ({ data }: ClientBlankEstimateTableProps) => {
	const [collapsedRows, setCollapsedRows] = useState<Set<string>>(new Set());

	const toggleRowCollapse = (problemId: string) => {
		setCollapsedRows((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(problemId)) {
				newSet.delete(problemId);
			} else {
				newSet.add(problemId);
			}
			return newSet;
		});
	};

	const dynamicColumns = useMemo(() => {
		const dates = Array.from(
			new Set(
				(data.problems ?? []).flatMap((problem) =>
					(problem?.estimate ?? []).filter((est) => est.date !== null).map((est) => est.date),
				),
			),
		).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

		return dates;
	}, [data.problems]);

	// const dynamicColumns = useMemo(() => {
	// 	const allDates = (data.problems ?? []).flatMap((problem) =>
	// 		(problem?.estimate ?? [])
	// 			.filter((est) => est.date !== null)
	// 			.map((est, idx) => ({ date: est.date, uniqueId: `${est.date}-${idx}` })),
	// 	);

	// 	// Сортируем все оценки по дате
	// 	const sortedDates = allDates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

	// 	return sortedDates;
	// }, [data.problems]);

	console.log(data.problems);

	const dateColumns: MRT_ColumnDef<any>[] = useMemo(
		() =>
			dynamicColumns.map((date, index) => ({
				id: `date-${date}`,
				accessorKey: date,
				// id: `date-${uniqueId}`,
				// accessorKey: uniqueId,
				header: date ? format(new Date(date), 'dd.MM.yyyy') : '',
				size: 96,
				maxSize: 96,
				Cell: ({ row }: any) => {
					if (row.original.isSection || row.original.isCollapsedRow) {
						return '';
					}

					// Находим все estimate с нужной датой и выбираем с максимальным ID
					const estimatesWithDate = row.original.estimate?.filter((est: Estimate) => est.date === date);
					const estimate = estimatesWithDate && estimatesWithDate.length > 0 
						? estimatesWithDate.reduce((latest: Estimate, current: Estimate) => {
							if (!latest) return current;
							return (current.id || 0) > (latest.id || 0) ? current : latest;
						})
						: null;

					if (!estimate || estimate.date === 0) {
						return '-';
					}

					// Cell: ({ row }: any) => {
					// 	if (row.original.isSection) {
					// 		return '';
					// 	}

					// 	// Важно: находим оценку по точному совпадению (с уникальным индексом)
					// 	const estimatesByDate = row.original.estimate?.filter((est: Estimate) => est.date === date);

					// 	// Находим нужную оценку по индексу колонки
					// 	const estimate = estimatesByDate?.[0];

					// 	if (!estimate || estimate.date === 0) {
					// 		return '-';
					// 	}

					return (
						<>
							{index === 0 && (
								<div className={styles.estimates}>
									<div className={styles.estimates__cell}>
										<div className={styles.estimates__cell__text}>Выполнение</div>
										<div className={styles.estimates__cell__grade}>{estimate.execution}</div>
									</div>
									<div className={styles.estimates__cell_satisfaction}>
										<div className={styles.estimates__cell_satisfaction__text}>
											Удовлетворенность
										</div>
										<div className={styles.estimates__cell_satisfaction__grade}>
											{estimate.satisfaction}
										</div>
									</div>
								</div>
							)}

							{index !== 0 && (
								<div className={styles.grades}>
									<div className={styles.grades__block}>
										<div className={styles.grades__block__execution}>{estimate.execution}</div>
									</div>

									<div className={styles.grades__block_sat}>
										<div className={styles.grades__block_sat__satisfaction}>
											{estimate.satisfaction}
										</div>
									</div>
								</div>
							)}
						</>
					);
				},
			})),
		[dynamicColumns, collapsedRows],
	);

	const columns = useMemo(
		() => [
			{
				id: 'expand',
				accessorKey: 'expand',
				header: '',
				Cell: ({ row }: any) => {
					if (row.original.isSection) {
						return null;
					}

					const problemId = row.original.problemId || `${row.original.id || row.index}`;
					const isCollapsed = collapsedRows.has(problemId);

					return (
						<div className={styles.expandButton} onClick={() => toggleRowCollapse(problemId)}>
							<Icon data={isCollapsed ? ChevronDown : ChevronUp} size={16} />
						</div>
					);
				},
				size: 40,
				maxSize: 40,
			},
			{
				id: 'importance',
				accessorKey: 'importance',
				header: 'Важн.',
				Cell: ({ row }: any) => <strong> {row.original.isSection ? '' : row.original.importance} </strong>,
				size: 73,
				maxSize: 73,
			},
			{
				id: 'parentSectionName',
				accessorKey: 'parentSectionName',
				header: 'Раздел',
				Cell: ({ row }: any) => {
					if (row.original.isSection) {
						return <strong>{row.original.parentSectionName}</strong>;
					}
					if (row.original.isCollapsedRow) {
						return <span style={{ paddingLeft: '20px' }}>{row.original.subsectionName}</span>;
					}
					return <span style={{ paddingLeft: '20px' }}>{row.original.subsectionName}</span>;
				},
				size: 180,
				maxSize: 180,
			},
			{
				id: 'text',
				accessorKey: 'text',
				header: 'Описание проблемы',
				size: 260,
				maxSize: 260,
				Cell: ({ row }: any) => {
					if (row.original.isSection || row.original.isCollapsedRow) {
						return null;
					}

					const problem = row.original;
					const mkfCode = problem.estimate?.[0];

					return (
						<div>
							<div>{problem.text}</div>
							<div className={styles.mkf}>
								<span className={styles.mkf__text}>МКФ-код: </span>
								<span className={styles.mkf__code}>
									{mkfCode && mkfCode.prefix && mkfCode.domainCode
										? `${mkfCode.prefix} ${mkfCode.domainCode}`
										: '-'}
								</span>
							</div>
						</div>
					);
				},
			},
			...dateColumns,
			{
				id: 'result',
				accessorKey: 'result',
				header: 'Результат',
				size: 82,
				maxSize: 82,
				Cell: ({ row }: any) => {
					if (row.original.isCollapsedRow) {
						return null;
					}

					const problem = row.original;

					const renderValueWithIcon = (value: number) => {
						let backgroundColor;
						let color;
						if (value > 0) {
							backgroundColor = 'rgba(50, 186, 118, 0.15)';
							color = 'rgba(45, 139, 93, 1)'; // Положительное значение
						} else if (value < 0) {
							backgroundColor = 'rgba(255, 219, 77, 0.3)';
							color = 'rgba(189, 162, 64, 1)'; // Отрицательное значение
						} else if (value <= -3) {
							backgroundColor = 'rgba(255, 0, 61, 0.15)';
							color = 'rgba(189, 9, 53, 1)'; // Отрицательное значение
						} else {
							backgroundColor = 'rgba(107, 132, 153, 0.15)'; // Нулевое значение
						}

						return (
							<div className={styles.result_block} style={{ backgroundColor, color }}>
								{value > 0 ? (
									<Icon data={ChevronsUp} size={12} />
								) : value < 0 ? (
									<Icon data={ChevronsDown} size={12} />
								) : value == 0 ? (
									<span>=</span>
								) : (
									<span>-</span>
								)}

								<span>{value > 0 ? `+${value}` : value}</span>
							</div>
						);
					};

					return (
						<div className={styles.results}>
							<div className={styles.results__container_execution}>
								<div className={styles.results__container_execution__result}>
									{renderValueWithIcon(problem.execution)}
								</div>
							</div>

							<div className={styles.results__container_satisfaction}>
								<div className={styles.results__container_satisfaction__result}>
									{renderValueWithIcon(problem.satisfaction)}
								</div>
							</div>
						</div>
					);
				},
			},
		],
		[dateColumns, collapsedRows],
	);

	const formattedData = useMemo(() => {
		const rows: any[] = [];
		const sections: { [key: string]: Problem[] } = {};

		(data.problems ?? []).forEach((problem) => {
			if (!sections[problem.parentSectionName]) {
				sections[problem.parentSectionName] = [];
			}
			sections[problem.parentSectionName].push(problem);
		});

		Object.entries(sections).forEach(([sectionName, problems]) => {
			// Добавляем заголовок секции
			rows.push({
				isSection: true,
				parentSectionName: sectionName,
				colSpan: columns.length + 1,
			});

			// Добавляем все проблемы (развернутые и свернутые)
			problems.forEach((problem, index) => {
				const problemId = `${problem.id || index}`;
				const isCollapsed = collapsedRows.has(problemId);

				if (isCollapsed) {
					// Добавляем строку-заглушку для свернутой проблемы
					rows.push({
						isCollapsedRow: true,
						problemId,
						importance: problem.importance,
						parentSectionName: problem.parentSectionName,
						subsectionName: problem.subsection?.sectionName || 'Нет подраздела',
						originalProblem: problem,
					});
				} else {
					// Добавляем полную строку для развернутой проблемы
					rows.push({
						...problem,
						subsectionName: problem.subsection?.sectionName || 'Нет подраздела',
						problemId,
					});
				}
			});
		});

		return rows;
	}, [columns.length, data.problems, collapsedRows]);

	return (
		<MaterialReactTable
			columns={columns}
			data={formattedData}
			enableSorting={false}
			enableColumnOrdering={false}
			enableColumnFilters={false}
			enableGlobalFilter={false}
			enableHiding={false}
			enableFullScreenToggle={false}
			enableColumnResizing={false}
			enableDensityToggle={false}
			enablePagination={false}
			enableColumnDragging={false}
			enableColumnActions={false}
			localization={Localization_RU}
			muiTableBodyCellProps={({ row, cell }) => {
				if (row.original.isSection && cell.column.id === 'parentSectionName') {
					return {
						colSpan: row.original.colSpan,
						style: {
							textAlign: 'left',
							paddingLeft: '121px', // Увеличил отступ для новой колонки
							backgroundColor: 'rgba(0, 0, 0, 0.05)',
							border: 'none',
						},
					};
				}
				if (row.original.isSection) {
					return {
						style: { display: 'none' },
					};
				}

				return {};
			}}
		/>
	);
};
